import { ApiService } from "@/services/api/ApiService";
import { AgentGroup } from "@/types/agent/agent-group";

export class AgentGroupService {
  static async listGroups(
    userId?: string,
    adminMode: boolean = false
  ): Promise<AgentGroup[]> {
    try {
      const response = await ApiService.invoke({
        resource: "genie-groups",
        action: "list",
        data: {
          adminMode,
          userId,
        },
      });

      return response?.groups || [];
    } catch (error) {
      console.error("Error fetching agent groups:", error);
      return [];
    }
  }

  static async getGroup(groupId: number, adminMode: boolean = false): Promise<AgentGroup | null> {
    try {
      const response = await ApiService.invoke({
        resource: "genie-groups",
        action: "get",
        id: groupId.toString(),
        data: {
          adminMode,
        },
      });

      return response?.group || null;
    } catch (error) {
      console.error("Error fetching agent group:", error);
      return null;
    }
  }

  static async createGroup(
    name: string,
    description?: string,
    userId?: string
  ): Promise<AgentGroup | null> {
    try {
      const response = await ApiService.invoke({
        resource: "genie-groups",
        action: "create",
        data: {
          name,
          description: description || null,
          userId,
        },
      });

      return response?.group || null;
    } catch (error) {
      console.error("Error creating agent group:", error);
      return null;
    }
  }

  static async updateGroup(
    groupId: number,
    updates: { name?: string; description?: string },
    userId?: string
  ): Promise<AgentGroup | null> {
    try {
      const response = await ApiService.invoke({
        resource: "genie-groups",
        action: "update",
        id: groupId.toString(),
        data: {
          ...updates,
          userId,
        },
      });

      return response?.group || null;
    } catch (error) {
      console.error("Error updating agent group:", error);
      return null;
    }
  }

  static async deleteGroup(groupId: number, userId?: string): Promise<boolean> {
    try {
      const response = await ApiService.invoke({
        resource: "genie-groups",
        action: "delete",
        id: groupId.toString(),
        data: {
          userId,
        },
      });

      return response?.success ?? false;
    } catch (error) {
      console.error("Error deleting agent group:", error);
      return false;
    }
  }

  static async reorderGroups(groupIds: number[], userId?: string): Promise<boolean> {
    try {
      const response = await ApiService.invoke({
        resource: "genie-groups",
        action: "reorder",
        data: {
          groupIds,
          userId,
        },
      });

      return response?.success ?? false;
    } catch (error) {
      console.error("Error reordering agent groups:", error);
      return false;
    }
  }

  static async getCountsByGroup(
    userId?: string,
    adminMode: boolean = false
  ): Promise<Record<string | number, number>> {
    try {
      const response = await ApiService.invoke({
        resource: "genie-groups",
        action: "counts",
        data: {
          adminMode,
          userId,
        },
      });

      return response?.counts || {};
    } catch (error) {
      console.error("Error fetching agent group counts:", error);
      return {};
    }
  }

  static async assignAgentToGroup(
    agentId: string,
    groupId: number | null,
    userId?: string
  ): Promise<boolean> {
    try {
      const response = await ApiService.invoke({
        resource: "genie-groups",
        action: "sync",
        data: {
          agentId,
          groupId,
          userId,
        },
      });

      return response?.agent !== undefined;
    } catch (error) {
      console.error("Error assigning agent to group:", error);
      return false;
    }
  }
}
